import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * BreadcrumbNav Component - مكون المسار المحسّن
 * يعرض مسار التنقل مع إمكانية النقر على كل جزء
 * الصفحة الحالية تظهر باللون الأصفر مع خط تحتها
 * 
 * @param {Array} items - مصفوفة من العناصر [{label_ar, label_en, path}]
 * آخر عنصر في المصفوفة هو الصفحة الحالية (لا يكون قابل للنقر)
 */
export default function BreadcrumbNav({ items = [] }) {
  const { language } = useLanguage();

  if (!items || items.length === 0) return null;

  return (
    <nav className="flex items-center gap-2 text-sm" dir="rtl">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const label = language === 'ar' ? item.label_ar : item.label_en;

        return (
          <span key={`${item.path || ''}-${item.label_en || item.label_ar || index}`} className="flex items-center gap-2">
            {isLast ? (
              // الصفحة الحالية - لون أصفر مع خط تحتها
              <span className="text-[#d4af37] font-medium border-b-2 border-[#d4af37] pb-0.5">
                {label}
              </span>
            ) : (
              // الصفحات السابقة - قابلة للنقر
              <>
                {item.path ? (
                  <Link 
                    to={item.path}
                    className="text-[#71717A] hover:text-white transition-colors"
                  >
                    {label}
                  </Link>
                ) : (
                  <span className="text-[#71717A]">{label}</span>
                )}
                <ChevronLeft className="h-4 w-4 text-[#71717A]" />
              </>
            )}
          </span>
        );
      })}
    </nav>
  );
}

/**
 * مسارات الصفحات المحددة مسبقاً
 * يمكن استخدامها مباشرة في المكونات
 */
export const BREADCRUMB_ITEMS = {
  // لوحة التحكم الرئيسية
  dashboard: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' }
  ],

  // الإدارة - ASB606 Admin
  management: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'الإدارة', label_en: 'Management', path: null }
  ],
  
  accountSettings: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'إعدادات الحساب', label_en: 'Account Settings', path: null }
  ],

  // الدعم
  support: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'الدعم والمساعدة', label_en: 'Support', path: '/support-center' }
  ],
  
  supportCenter: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'الدعم والمساعدة', label_en: 'Support', path: '/support-center' },
    { label_ar: 'مركز الدعم', label_en: 'Support Center', path: null }
  ],

  // التحكم والمعلومات للمنصات - الزر الرئيسي
  platformControl: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التحكم والمعلومات للمنصات', label_en: 'Platform Control & Info', path: '/dashboard?view=platform-control' }
  ],

  // التحكم والمعلومات - CLOUDGM FZ LLC
  controlCloudGM: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التحكم والمعلومات للمنصات', label_en: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
    { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
    { label_ar: 'منصة العملاء (التحكم)', label_en: 'Customers Platform (Control)', path: null },
    { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: null }
  ],

  // التحكم والمعلومات - منصة التجار
  controlCloudGMBusiness: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التحكم والمعلومات للمنصات', label_en: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
    { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
    { label_ar: 'منصة التجار (التحكم)', label_en: 'Merchants Platform (Control)', path: null },
    { label_ar: 'التحكم والمعلومات (منصة التجار)', label_en: 'Control & Info (Merchants)', path: null }
  ],

  // التحكم بلوحة التحكم - ASB606 ADMIN
  controlPanelSettings: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التحكم والمعلومات للمنصات', label_en: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
    { label_ar: 'ASB606 ADMIN (للتحكم)', label_en: 'ASB606 ADMIN (Control)', path: null },
    { label_ar: 'التحكم بلوحة التحكم', label_en: 'Control Panel Settings', path: null }
  ],
  
  // FZ LC - منصة العملاء
  customerPlatform: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
    { label_ar: 'منصة العملاء', label_en: 'Customers Platform', path: null }
  ],
  
  // FZ LC - منصة التجار
  merchantPlatform: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
    { label_ar: 'منصة التجار', label_en: 'Merchants Platform', path: null }
  ],

  storeTypes: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التحكم والمعلومات للمنصات', label_en: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
    { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
    { label_ar: 'التحكم والمعلومات (منصة التجار)', label_en: 'Control & Info (Merchants)', path: '/control/cloudgm-business' },
    { label_ar: 'التحكم بأنواع المتاجر', label_en: 'Store Types Control', path: null }
  ],

  companySetup: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
    { label_ar: 'منصة العملاء', label_en: 'Customers Platform', path: '/dashboard?view=cloudgm&platform=customers' },
    { label_ar: 'التحكم والمعلومات', label_en: 'Control & Info', path: '/control/cloudgm' },
    { label_ar: 'تأسيس الشركات', label_en: 'Company Setup', path: null }
  ],

  // المالية
  finance: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' }
  ],
  
  loyaltyPoints: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
    { label_ar: 'نقاط الولاء', label_en: 'Loyalty Points', path: null }
  ],
  
  redeemCodes: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
    { label_ar: 'أكواد الاسترداد', label_en: 'Redeem Codes', path: null }
  ],

  // التسويق
  marketing: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التسويق', label_en: 'Marketing', path: '/marketing' }
  ],
  
  cgmCoin: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التسويق', label_en: 'Marketing', path: '/marketing' },
    { label_ar: 'CLOUDGM COIN', label_en: 'CLOUDGM COIN', path: null }
  ],

  cgmCoinSubscribers: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التسويق', label_en: 'Marketing', path: '/marketing' },
    { label_ar: 'CLOUDGM COIN', label_en: 'CLOUDGM COIN', path: '/dashboard/cgm-coin' },
    { label_ar: 'المسجلين مسبقاً', label_en: 'Pre-registrants', path: null }
  ],

  // الإشراف على المنصات
  platforms: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'الإشراف على المنصات', label_en: 'Platform Supervision', path: '/dashboard' }
  ],

  // الفئات
  categories: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التحكم والمعلومات للمنصات', label_en: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
    { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
    { label_ar: 'التحكم والمعلومات (منصة التجار)', label_en: 'Control & Info (Merchants)', path: '/control/cloudgm-business' },
    { label_ar: 'إدارة تصنيفات المتاجر', label_en: 'Store Categories Management', path: null }
  ],
  
  // 606 Fitness
  fitness: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: '606 Fitness', label_en: '606 Fitness', path: '/dashboard/606-fitness' }
  ],
  
  fitnessMembers: [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: '606 Fitness', label_en: '606 Fitness', path: '/dashboard/606-fitness' },
    { label_ar: 'إدارة الأعضاء', label_en: 'Members Management', path: null }
  ],
};
